import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Injectable } from '@angular/core';

/**
 * A service to manage and validate uniqueness of form controls
 * (e.g. email, phone) across dynamically registered fields.
 */
@Injectable()
export class SelfDisclosureUniqueFieldsService {
  // Stores controls grouped by their unique field key (e.g., 'email', 'phone')
  private controlsMap = new Map<
    string,
    Set<AbstractControl<unknown, unknown>>
  >();

  // Keeps track of which keys currently have active uniqueness validation
  private activeKeys = new Set<string>();

  /**
   * Registers a form control under a given key.
   * If validation for that key is already enabled, the validator is applied immediately.
   *
   * @param key Unique key to group controls (e.g., 'email')
   * @param control The form control to register
   */
  register(key: string, control: AbstractControl<unknown, unknown>): void {
    if (!this.controlsMap.has(key)) {
      this.controlsMap.set(key, new Set());
    }

    const controlSet = this.controlsMap.get(key);
    controlSet.add(control);

    if (this.activeKeys.has(key)) {
      control.addValidators(this.uniquenessValidator(key, control));
      control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
  }

  /**
   * Unregisters a form control for a given key.
   * Triggers revalidation of all remaining controls under that key.
   *
   * @param key The key the control was registered under
   * @param control The control to remove
   */
  unregister(key: string, control: AbstractControl<unknown, unknown>): void {
    const set = this.controlsMap.get(key);
    if (set) {
      set.delete(control);
      this.revalidateAll(key);
    }
  }

  /**
   * Enables uniqueness validation for all registered controls under a given key.
   *
   * @param key The key (e.g., 'email') for which to activate validation
   */
  enableValidation(key: string): void {
    if (this.activeKeys.has(key)) return;

    this.activeKeys.add(key);

    const controls = this.controlsMap.get(key);
    if (!controls) return;

    for (const control of controls) {
      control.addValidators(this.uniquenessValidator(key, control));
    }

    this.revalidateAll(key);
  }

  /**
   * Revalidates all controls under the specified key.
   * Used when a control is removed or validation is enabled.
   *
   * @param key The key for which to revalidate all associated controls
   */
  private revalidateAll(key: string): void {
    const controls = this.controlsMap.get(key);
    if (!controls) return;

    for (const control of controls) {
      control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
  }

  /**
   * Creates a validator function that checks for duplicate values
   * among all controls registered under the same key.
   *
   * @param key The key under which uniqueness is enforced
   * @param selfControl The control the validator is attached to
   * @returns A ValidatorFn that returns an error if a duplicate is found
   */
  private uniquenessValidator(
    key: string,
    selfControl: AbstractControl<unknown, unknown>
  ): ValidatorFn {
    return (): ValidationErrors | null => {
      const controls = this.controlsMap.get(key);
      if (
        !controls ||
        !selfControl.value ||
        typeof selfControl.value !== 'string'
      )
        return null;

      const currentValue = selfControl.value?.toLowerCase().trim();
      if (!currentValue) return null;

      let count = 0;
      for (const c of controls) {
        if (
          typeof c.value === 'string' &&
          c.value?.toLowerCase().trim() === currentValue
        ) {
          count++;
        }
      }

      return count > 1 ? { duplicateValue: true } : null;
    };
  }
}
